import { Coffee, Info } from 'lucide-react';
import type { ReactNode } from 'react';
import Button from '@components/ui/Button';
import Icon from '@components/ui/Icon';
import { useUIStore } from '@stores/useUIStore';
import { track } from '@utils/analytics';

/**
 * TopBar — the fixed 56px header at the top of the app shell (DESIGN.md
 * §5 · layout structure, z-index: 100).
 *
 * Structure: logo · wordmark + subtitle · divider · freeware tagline ·
 * [About] · [Support ↗]. The logo click and the About button both open
 * the About modal; Support links out to Ko-fi.
 */
export interface TopBarProps {
  /** Right-aligned slot. Render your donate button / toolbar here. */
  rightSlot?: ReactNode;
}

const KOFI_URL = 'https://ko-fi.com/studioshell';

export default function TopBar({ rightSlot }: TopBarProps) {
  const showAbout = useUIStore((s) => s.showAbout);
  return (
    <header
      role="banner"
      style={{
        position: 'relative',
        flexShrink: 0,
        height: 'var(--topbar-height)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-md)',
        padding: '0 var(--space-xl)',
        background: 'var(--bg-panel)',
        borderBottom: '1px solid var(--border-dim)',
        zIndex: 100,
      }}
    >
      {/* Logo mark — green square with the "S" glyph. Click opens About. */}
      <button
        type="button"
        onClick={showAbout}
        aria-label="About StudioShell"
        style={{
          width: 28,
          height: 28,
          flexShrink: 0,
          borderRadius: 'var(--radius-md)',
          background: 'var(--accent-green)',
          display: 'grid',
          placeItems: 'center',
          fontFamily: 'var(--font-mono)',
          fontWeight: 700,
          fontSize: 15,
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 0 20px rgba(112, 148, 36, 0.15)',
        }}
      >
        S
      </button>

      {/* Wordmark + subtitle */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 18,
            letterSpacing: '-0.3px',
            color: 'var(--accent-green)',
            lineHeight: 1,
          }}
        >
          StudioShell
        </div>
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 11,
            fontWeight: 500,
            letterSpacing: '1.5px',
            textTransform: 'uppercase',
            color: 'var(--text-muted)',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}
        >
          Acoustic Room Simulator
        </div>
      </div>

      {/* Divider + freeware footer note */}
      <div
        aria-hidden
        style={{
          width: 1,
          alignSelf: 'stretch',
          background: 'var(--border-dim)',
          margin: '0 4px',
        }}
      />
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--text-hint)',
          letterSpacing: 0,
          whiteSpace: 'nowrap',
        }}
      >
        free · donation-supported · no signup
      </span>

      <div style={{ flex: 1 }} />

      {/* Action buttons */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-sm)',
        }}
      >
        <Button
          variant="secondary"
          size="sm"
          icon={<Icon icon={Info} size={12} color="var(--text-muted)" />}
          onClick={showAbout}
        >
          About
        </Button>
        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Support on Ko-fi"
          className="btn-base btn-base--sm btn-primary"
          style={{ textDecoration: 'none' }}
          onClick={() => track('donation_clicked', { location: 'topbar' })}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Icon icon={Coffee} size={14} />
          </span>
          <span>
            Support <span style={{ opacity: 0.7, marginLeft: 2 }}>↗</span>
          </span>
        </a>
        {rightSlot}
      </div>
    </header>
  );
}
